/**
 * Entity Membership System Enums and Constants
 * Issue #293 - Permission System Enhancement
 *
 * Provides unified membership tracking for all entity types (cases, deals, consumers, custom)
 */

/**
 * Permissions that can be granted to entity roles
 * These are entity-scoped permissions (different from global API permissions)
 */
export enum EntityMembershipPermission {
  // Core CRUD permissions
  VIEW = 'view',
  EDIT = 'edit',
  DELETE = 'delete',

  // Membership management
  ASSIGN_MEMBERS = 'assign_members',
  REMOVE_MEMBERS = 'remove_members',
  CHANGE_ROLES = 'change_roles',

  // Workflow permissions
  CHANGE_STATUS = 'change_status',
  CHANGE_STAGE = 'change_stage',
  APPROVE = 'approve',

  // Data interaction permissions
  ADD_COMMENTS = 'add_comments',
  ADD_DOCUMENTS = 'add_documents',
  VIEW_HISTORY = 'view_history',
  EXPORT = 'export',

  // Admin permissions
  CONFIGURE_NOTIFICATIONS = 'configure_notifications',
  BULK_OPERATIONS = 'bulk_operations',
}

/**
 * Who the membership targets
 */
export enum MembershipTargetType {
  USER = 'USER',
  TEAM = 'TEAM',
}

/**
 * Actions tracked in membership audit log
 */
export enum MembershipAction {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
  ROLE_CHANGED = 'ROLE_CHANGED',
  PERMISSIONS_UPDATED = 'PERMISSIONS_UPDATED',
}

/**
 * Standard entity role names
 */
export enum EntityRoleName {
  OWNER = 'owner',
  MANAGER = 'manager',
  PROCESSOR = 'processor',
  COLLABORATOR = 'collaborator',
  OBSERVER = 'observer',
  REVIEWER = 'reviewer',
}

/**
 * Notification channels for membership notifications
 * Note: Renamed to avoid conflict with NotificationChannel in notification.enums.ts
 */
export enum MembershipNotificationChannel {
  EMAIL = 'email',
  PUSH = 'push',
  IN_APP = 'in_app',
  SMS = 'sms',
}

/**
 * Notification frequency options
 */
export enum NotificationFrequency {
  IMMEDIATE = 'immediate',
  DAILY_DIGEST = 'daily_digest',
  WEEKLY_DIGEST = 'weekly_digest',
}

/**
 * Standard notification events that can be subscribed to for memberships
 * Note: Renamed to avoid conflict with NotificationEvent in notification.enums.ts
 */
export enum MembershipNotificationEvent {
  STATUS_CHANGE = 'status_change',
  STAGE_CHANGE = 'stage_change',
  COMMENT_ADDED = 'comment_added',
  DOCUMENT_UPLOADED = 'document_uploaded',
  MEMBER_ADDED = 'member_added',
  MEMBER_REMOVED = 'member_removed',
  ASSIGNMENT_CHANGED = 'assignment_changed',
  DUE_DATE_APPROACHING = 'due_date_approaching',
  OVERDUE = 'overdue',
}

/**
 * Inheritance source types for tracking how memberships were created
 */
export enum MembershipInheritanceSource {
  CREATOR = 'creator',
  MANAGER_CHAIN = 'manager', // Format: "manager:{userId}"
  TEAM = 'team', // Format: "team:{teamId}"
  BULK_OPERATION = 'bulk', // Format: "bulk:{operationId}"
  MANUAL = 'manual',
}

/**
 * Default permissions for each standard role
 * These can be customized per business via EntityRole model
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<EntityRoleName, EntityMembershipPermission[]> = {
  [EntityRoleName.OWNER]: [
    EntityMembershipPermission.VIEW,
    EntityMembershipPermission.EDIT,
    EntityMembershipPermission.DELETE,
    EntityMembershipPermission.ASSIGN_MEMBERS,
    EntityMembershipPermission.REMOVE_MEMBERS,
    EntityMembershipPermission.CHANGE_ROLES,
    EntityMembershipPermission.CHANGE_STATUS,
    EntityMembershipPermission.CHANGE_STAGE,
    EntityMembershipPermission.APPROVE,
    EntityMembershipPermission.ADD_COMMENTS,
    EntityMembershipPermission.ADD_DOCUMENTS,
    EntityMembershipPermission.VIEW_HISTORY,
    EntityMembershipPermission.EXPORT,
    EntityMembershipPermission.CONFIGURE_NOTIFICATIONS,
    EntityMembershipPermission.BULK_OPERATIONS,
  ],
  [EntityRoleName.MANAGER]: [
    EntityMembershipPermission.VIEW,
    EntityMembershipPermission.EDIT,
    EntityMembershipPermission.ASSIGN_MEMBERS,
    EntityMembershipPermission.CHANGE_STATUS,
    EntityMembershipPermission.CHANGE_STAGE,
    EntityMembershipPermission.ADD_COMMENTS,
    EntityMembershipPermission.ADD_DOCUMENTS,
    EntityMembershipPermission.VIEW_HISTORY,
    EntityMembershipPermission.EXPORT,
  ],
  [EntityRoleName.PROCESSOR]: [
    EntityMembershipPermission.VIEW,
    EntityMembershipPermission.EDIT,
    EntityMembershipPermission.ADD_COMMENTS,
    EntityMembershipPermission.ADD_DOCUMENTS,
    EntityMembershipPermission.VIEW_HISTORY,
  ],
  [EntityRoleName.COLLABORATOR]: [
    EntityMembershipPermission.VIEW,
    EntityMembershipPermission.EDIT,
    EntityMembershipPermission.ADD_COMMENTS,
    EntityMembershipPermission.ADD_DOCUMENTS,
  ],
  [EntityRoleName.OBSERVER]: [EntityMembershipPermission.VIEW, EntityMembershipPermission.VIEW_HISTORY],
  [EntityRoleName.REVIEWER]: [
    EntityMembershipPermission.VIEW,
    EntityMembershipPermission.APPROVE,
    EntityMembershipPermission.ADD_COMMENTS,
    EntityMembershipPermission.VIEW_HISTORY,
  ],
};

/**
 * Standard entity types that support membership
 */
export const STANDARD_ENTITY_TYPES = {
  CASE: 'case',
  DEAL: 'deal',
  CONSUMER: 'consumer',
  WILDCARD: '*', // Applies to all entity types
} as const;

/**
 * Get custom entity type string for EntityDefinition
 * @param entityDefinitionId The ID of the custom entity definition
 */
export function getCustomEntityType(entityDefinitionId: string): string {
  return `custom:${entityDefinitionId}`;
}

/**
 * Check if an entity type is a custom entity
 */
export function isCustomEntityType(entityType: string): boolean {
  return entityType.startsWith('custom:');
}

/**
 * Extract entity definition ID from custom entity type
 */
export function getEntityDefinitionId(customEntityType: string): string | null {
  if (!isCustomEntityType(customEntityType)) {
    return null;
  }
  return customEntityType.slice('custom:'.length);
}

/**
 * Default notification preferences for new memberships
 */
export const DEFAULT_NOTIFICATION_PREFERENCES = {
  channels: [MembershipNotificationChannel.IN_APP],
  events: [
    MembershipNotificationEvent.STATUS_CHANGE,
    MembershipNotificationEvent.COMMENT_ADDED,
    MembershipNotificationEvent.MEMBER_ADDED,
  ],
  frequency: NotificationFrequency.IMMEDIATE,
};

/**
 * Role assignment rules - which roles can assign which other roles
 */
export const ROLE_ASSIGNMENT_RULES: Record<EntityRoleName, EntityRoleName[]> = {
  [EntityRoleName.OWNER]: [
    EntityRoleName.OWNER,
    EntityRoleName.MANAGER,
    EntityRoleName.PROCESSOR,
    EntityRoleName.COLLABORATOR,
    EntityRoleName.OBSERVER,
    EntityRoleName.REVIEWER,
  ],
  [EntityRoleName.MANAGER]: [
    EntityRoleName.PROCESSOR,
    EntityRoleName.COLLABORATOR,
    EntityRoleName.OBSERVER,
    EntityRoleName.REVIEWER,
  ],
  [EntityRoleName.PROCESSOR]: [EntityRoleName.OBSERVER],
  [EntityRoleName.COLLABORATOR]: [EntityRoleName.OBSERVER],
  [EntityRoleName.OBSERVER]: [],
  [EntityRoleName.REVIEWER]: [],
};

// =============================================================================
// ENTERPRISE PERMISSION FEATURES (Issue #293 - Gap Analysis)
// =============================================================================

/**
 * Access request status for self-service access workflow
 */
export enum AccessRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

/**
 * Guest user types for external collaborators
 */
export enum GuestType {
  EXTERNAL_COLLABORATOR = 'EXTERNAL_COLLABORATOR',
  PARTNER = 'PARTNER',
  CLIENT = 'CLIENT',
  VENDOR = 'VENDOR',
}

/**
 * Permission condition types for conditional permissions
 */
export enum PermissionConditionType {
  FIELD_VALUE = 'field_value',
  CREATED_BY = 'created_by',
  ASSIGNED_TO = 'assigned_to',
  TIME_BASED = 'time_based',
  STATUS = 'status',
  STAGE = 'stage',
  AMOUNT = 'amount',
}

/**
 * Condition operators for field-based conditions
 */
export enum ConditionOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  GREATER_OR_EQUAL = 'greater_or_equal',
  LESS_OR_EQUAL = 'less_or_equal',
  IN = 'in',
  NOT_IN = 'not_in',
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',
}

/**
 * Condition effect - whether condition allows or denies
 */
export enum ConditionEffect {
  ALLOW = 'allow',
  DENY = 'deny',
}

/**
 * Field permission level for role-based field visibility
 */
export enum FieldPermissionLevel {
  HIDDEN = 'hidden',
  VIEW = 'view',
  EDIT = 'edit',
  MASKED = 'masked',
}

/**
 * Share link permission presets
 */
export const SHARE_LINK_PERMISSION_PRESETS = {
  VIEW_ONLY: ['view'],
  COMMENT: ['view', 'add_comments'],
  EDIT: ['view', 'edit', 'add_comments'],
  FULL: ['view', 'edit', 'add_comments', 'add_documents'],
} as const;

/**
 * Cross-entity permission relationship paths
 */
export const RELATIONSHIP_PATHS = {
  DEAL_TO_CASES: 'deal.cases',
  CASE_TO_CONSUMER: 'case.consumer',
  CONSUMER_TO_CASES: 'consumer.cases',
  CASE_TO_DOCUMENTS: 'case.documents',
} as const;

/**
 * Default guest restrictions
 */
export const DEFAULT_GUEST_RESTRICTIONS = {
  canSeeTeamMembers: false,
  canSeeOrgChart: false,
  canInviteOthers: false,
  maxEntitiesAccess: 50, // Max entities a guest can be added to
};

/**
 * Access request expiration defaults (in days)
 */
export const ACCESS_REQUEST_DEFAULTS = {
  pendingExpirationDays: 7,
  defaultAccessDurationDays: 90,
};

/**
 * Common condition templates for conditional permissions
 */
export const CONDITION_TEMPLATES = {
  EDIT_ONLY_DRAFT: {
    conditionType: PermissionConditionType.FIELD_VALUE,
    condition: { field: 'status', operator: ConditionOperator.EQUALS, value: 'Draft' },
    effect: ConditionEffect.ALLOW,
  },
  DELETE_OWN_ONLY: {
    conditionType: PermissionConditionType.CREATED_BY,
    condition: { field: 'createdBy', operator: ConditionOperator.EQUALS, value: '$currentUser' },
    effect: ConditionEffect.ALLOW,
  },
  BUSINESS_HOURS_ONLY: {
    conditionType: PermissionConditionType.TIME_BASED,
    condition: { operator: 'time_range', start: '09:00', end: '17:00', timezone: 'America/New_York' },
    effect: ConditionEffect.ALLOW,
  },
  AMOUNT_LIMIT: {
    conditionType: PermissionConditionType.AMOUNT,
    condition: { field: 'amount', operator: ConditionOperator.LESS_THAN, value: 500000 },
    effect: ConditionEffect.ALLOW,
  },
};
